/* Vanya global theme JS — framework-free, ported from the Next.js components.
   Each behaviour is self-contained and guarded so sections work independently. */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------------------------------------------------------
   * RotatingHero — scroll-driven ring rotation (ported from RotatingHero.tsx)
   * Sets a CSS variable; the transform lives in CSS.
   * ------------------------------------------------------------- */
  function initRotatingRing() {
    var ring = document.querySelector("[data-rotating-ring]");
    if (!ring || reduceMotion) return;

    var current = 0;
    var target = 0;
    var rafId;

    function onScroll() {
      target = -window.scrollY * 0.25;
    }
    function update() {
      current += (target - current) * 0.1;
      ring.style.setProperty("--ring-rotate", current.toFixed(2) + "deg");
      rafId = requestAnimationFrame(update);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    rafId = requestAnimationFrame(update);
  }

  /* ---------------------------------------------------------------
   * SwarnaHero — mouse-move parallax (ported from SwarnaHero.tsx)
   * JS only writes --px / --py; layers read them via calc() in CSS.
   * ------------------------------------------------------------- */
  function initParallax() {
    var section = document.querySelector("[data-parallax-section]");
    if (!section) return;
    var hasMouse = window.matchMedia("(pointer: fine)").matches;
    if (!hasMouse || reduceMotion) return;

    var targetX = 0, targetY = 0, curX = 0, curY = 0, rafId;

    section.addEventListener("mousemove", function (e) {
      var rect = section.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    }, { passive: true });
    section.addEventListener("mouseleave", function () {
      targetX = 0; targetY = 0;
    }, { passive: true });

    function loop() {
      curX += (targetX - curX) * 0.075;
      curY += (targetY - curY) * 0.075;
      section.style.setProperty("--px", curX.toFixed(4));
      section.style.setProperty("--py", curY.toFixed(4));
      rafId = requestAnimationFrame(loop);
    }
    rafId = requestAnimationFrame(loop);
  }

  /* ---------------------------------------------------------------
   * SwarnaHero slider (ported from SwarnaHero.tsx activeSlide state)
   * Syncs slides, thumbs, per-slide content, float + banner layers,
   * pagination dots. Autoplay via data-autoplay seconds (0 = off).
   * ------------------------------------------------------------- */
  function initSwarnaHero() {
    document.querySelectorAll("[data-swarna-hero]").forEach(function (root) {
      var slides = root.querySelectorAll("[data-swarna-slide]");
      if (!slides.length) return;

      var total = slides.length;
      var active = 0;
      var timer = null;
      var seconds = parseFloat(root.getAttribute("data-autoplay")) || 0;

      function show(idx) {
        active = (idx + total) % total;

        slides.forEach(function (el) {
          var i = parseInt(el.getAttribute("data-swarna-slide"), 10);
          el.setAttribute("data-active", i === active ? "true" : "false");
        });
        root.querySelectorAll("[data-swarna-content]").forEach(function (el) {
          var i = parseInt(el.getAttribute("data-swarna-content"), 10);
          var on = i === active;
          el.setAttribute("data-active", on ? "true" : "false");
          el.style.display = on ? "" : "none";
        });
        root.querySelectorAll("[data-swarna-float]").forEach(function (el) {
          var i = parseInt(el.getAttribute("data-swarna-float"), 10);
          el.setAttribute("data-active", i === active ? "true" : "false");
        });
        root.querySelectorAll("[data-swarna-banner]").forEach(function (el) {
          var i = parseInt(el.getAttribute("data-swarna-banner"), 10);
          el.setAttribute("data-active", i === active ? "true" : "false");
        });
        root.querySelectorAll("[data-swarna-goto]").forEach(function (el) {
          var i = parseInt(el.getAttribute("data-swarna-goto"), 10);
          el.setAttribute("aria-current", i === active ? "true" : "false");
        });
      }

      function schedule() {
        if (!seconds) return;
        clearTimeout(timer);
        timer = setTimeout(function () { show(active + 1); schedule(); }, seconds * 1000);
      }

      root.querySelectorAll("[data-swarna-goto]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          show(parseInt(btn.getAttribute("data-swarna-goto"), 10));
          schedule();
        });
      });

      show(0);
      schedule();
    });
  }

  /* ---------------------------------------------------------------
   * Swarna Showcase — active column + full-bleed background crossfade
   * (ported from SwarnaShowcase.tsx activeIndex). Hover/click a column:
   * its image fills the whole section, that column shows its detail.
   * ------------------------------------------------------------- */
  function initSwarnaShowcase() {
    document.querySelectorAll("[data-swarna-showcase]").forEach(function (root) {
      var cols = root.querySelectorAll("[data-showcase-col]");
      var layers = root.querySelectorAll("[data-showcase-bg] .bg-layer");
      if (!cols.length) return;

      function activate(idx) {
        cols.forEach(function (c) {
          c.setAttribute("data-active", parseInt(c.getAttribute("data-col-index"), 10) === idx ? "true" : "false");
        });
        layers.forEach(function (l) {
          l.setAttribute("data-active", parseInt(l.getAttribute("data-bg-index"), 10) === idx ? "true" : "false");
        });
      }

      cols.forEach(function (c) {
        var idx = parseInt(c.getAttribute("data-col-index"), 10);
        c.addEventListener("mouseenter", function () { activate(idx); });
        c.addEventListener("click", function () { activate(idx); });
      });

      activate(0);
    });
  }

  /* ---------------------------------------------------------------
   * Mobile menu drawer (ported from Header.tsx)
   * ------------------------------------------------------------- */
  function initMobileMenu() {
    var openBtn = document.querySelector("[data-menu-open]");
    var closeEls = document.querySelectorAll("[data-menu-close]");
    var drawer = document.querySelector("[data-menu-drawer]");
    if (!openBtn || !drawer) return;

    function open() {
      drawer.classList.add("is-open");
      document.body.style.overflow = "hidden";
      openBtn.setAttribute("aria-expanded", "true");
    }
    function close() {
      drawer.classList.remove("is-open");
      document.body.style.overflow = "";
      openBtn.setAttribute("aria-expanded", "false");
    }
    openBtn.addEventListener("click", open);
    closeEls.forEach(function (el) { el.addEventListener("click", close); });
    window.addEventListener("keydown", function (e) {
      if (e.key === "Escape") close();
    });
  }

  /* ---------------------------------------------------------------
   * Carousel arrows (ported from TrendyCollection.tsx scroll())
   * ------------------------------------------------------------- */
  function initCarousels() {
    document.querySelectorAll("[data-carousel]").forEach(function (root) {
      var track = root.querySelector("[data-carousel-track]");
      if (!track) return;
      function step() {
        var card = track.querySelector(".tr-card, [data-carousel-item]");
        if (card) {
          var styles = window.getComputedStyle(track);
          var gap = parseFloat(styles.columnGap || styles.gap) || 20;
          return card.getBoundingClientRect().width + gap;
        }
        return track.clientWidth * 0.8;
      }
      root.querySelectorAll("[data-carousel-prev]").forEach(function (b) {
        b.addEventListener("click", function () {
          track.scrollBy({ left: -step(), behavior: "smooth" });
        });
      });
      root.querySelectorAll("[data-carousel-next]").forEach(function (b) {
        b.addEventListener("click", function () {
          track.scrollBy({ left: step(), behavior: "smooth" });
        });
      });
    });
  }

  /* ---------------------------------------------------------------
   * Instagram feed tabs (ported from InstagramSection.tsx activeTab)
   * Toggles POSTS (all) vs REELS (reels only), updates grid columns.
   * ------------------------------------------------------------- */
  function initInstagramTabs() {
    document.querySelectorAll("[data-ig-section]").forEach(function (root) {
      var tabs = root.querySelectorAll("[data-ig-tab]");
      var grid = root.querySelector("[data-ig-grid]");
      var items = root.querySelectorAll("[data-ig-item]");
      if (!tabs.length || !grid) return;

      function apply(tab) {
        tabs.forEach(function (t) {
          t.setAttribute("aria-current", t.getAttribute("data-ig-tab") === tab ? "true" : "false");
        });
        grid.setAttribute("data-ig-view", tab);
        items.forEach(function (el) {
          var isReel = el.getAttribute("data-ig-type") === "reel";
          el.style.display = tab === "REELS" && !isReel ? "none" : "";
        });
      }

      tabs.forEach(function (t) {
        t.addEventListener("click", function () { apply(t.getAttribute("data-ig-tab")); });
      });
      apply("POSTS");
    });
  }

  function init() {
    initRotatingRing();
    initParallax();
    initSwarnaHero();
    initSwarnaShowcase();
    initInstagramTabs();
    initMobileMenu();
    initCarousels();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
